import * as React from "react";
import { setUserObject } from "@grandvisionhq/analytics";
import { Actions, Selectors, State, useMyAccountMenuItems, MyAccountMenuPosition, MyAccountMenu } from "@grandvisionhq/auth";
import { useLocation } from "@grandvisionhq/frk-routing";
import { getLocaleSwitcherMenuLabel, getLocaleSwitcherData } from "@grandvisionhq/layout";
import { useIntl, useFeatureToggles, useDispatch, useSelector } from "@grandvisionhq/state";
import { CmsHeader as CmsHeader$1 } from "../../../../components/Header/components/Header/Header.js";
import { useMeta } from "../../../../context/context/MetaContext.js";
import { getMenuItems } from "../../../../utils/utils/get-menu-items.js";
import { useBrPageContext, useBrComponentContext } from "../../../../context/context/context.js";
import { useBloomreachPageModels } from "../../../../hooks/hooks/use-bloomreach-page-models.js";
import { useBloomreachAppointmentURLsConfiguration } from "../../../../hooks/hooks/use-bloomreach-appointment-urls-configuration.js";
import { useBloomreachSiteLogos } from "../../../../hooks/hooks/use-bloomreach-site-logos.js";
import { useBloomreachPageRoutes } from "../../../../hooks/hooks/use-bloomreach-page-routes.js";
import { getUrl } from "../../../../utils/utils/get-url.js";
const CmsHeader = (props) => {
  const intl = useIntl();
  const page = useBrPageContext();
  const component = useBrComponentContext();
  const { isMultilingualEnabled, isMyAccountEnabled, isAABEnabled, isWishlistEnabled } = useFeatureToggles();
  const { site, bloomreach, algolia } = useMeta();
  const siteLogos = useBloomreachSiteLogos();
  const isPreview = Boolean(page?.isPreview());
  const { routes: pageRoutes } = useBloomreachPageRoutes();
  const { pathname } = useLocation();
  const siteName = site?.name ?? "Unknown";
  const logoLink = getUrl(pageRoutes.root, page) ?? "/";
  const dispatch = useDispatch();
  const logout = React.useCallback(
    () => dispatch(Actions.LogoutActions.logOut(pathname)),
    [dispatch, pathname]
  );
  const recoverSession = React.useCallback(
    () => dispatch(Actions.LoginActions.recoverSession()),
    [dispatch]
  );
  const auth = useSelector(Selectors.getAuth);
  const formattedRoutes = useSelector(Selectors.getFormattedRoutes);
  const [mounted, setMounted] = React.useState(false);
  const pageModels = useBloomreachPageModels();
  const { routes = {}, localeOptions, countrySwitcher } = pageModels ?? {};
  const { menuItems: topMenuItems, menu: topMenu } = getMenuItems("top-menu", component, page);
  const { menu: appointmentMenu, menuItems: appointmentMenuItems } = getMenuItems(
    "appointment-menu",
    component,
    page
  );
  const { menu: mainMenu, menuItems: mainMenuItems } = getMenuItems("main-menu", component, page);
  const appointmentMenuItemsWithAABconfig = useBloomreachAppointmentURLsConfiguration(appointmentMenuItems);
  const countrySwitcherContent = countrySwitcher && page?.getContent(countrySwitcher);
  const countrySwitcherOptions = getCountrySwitcherUrls(
    countrySwitcherContent?.getData()
  );
  const headerLogo = React.useMemo(
    () => siteLogos.main ? {
      url: siteLogos.main,
      altText: siteName
    } : void 0,
    [siteLogos.main, siteName]
  );
  const headerLogoMobile = React.useMemo(
    () => siteLogos.mobile ? {
      url: siteLogos.mobile,
      altText: siteName
    } : void 0,
    [siteLogos.mobile, siteName]
  );
  const authRef = React.useRef(auth);
  const isUserLoggedIn = State.getLoggedInState(auth) === "logged-in";
  const myAccountMenuItems = useMyAccountMenuItems({
    logout,
    position: MyAccountMenuPosition.Header,
    routes: formattedRoutes,
    isUserLoggedIn,
    isAabEnabled: isAABEnabled,
    isWishlistEnabled
  }).map((item) => ({
    ...item,
    link: item.link ? {
      ...item.link,
      onClick: void 0
    } : void 0
  }));
  const myAccountButton = React.useMemo(() => {
    if (!isMyAccountEnabled || !formattedRoutes) {
      return null;
    }
    return /* @__PURE__ */ React.createElement(MyAccountMenu, { menuItems: myAccountMenuItems });
  }, [myAccountMenuItems, isMyAccountEnabled, formattedRoutes]);
  const ordersUrl = React.useMemo(
    // ordersUrl should be defined when my-account is enabled and the orders route exists
    () => isMyAccountEnabled ? formattedRoutes?.orders : void 0,
    [formattedRoutes?.orders, isMyAccountEnabled]
  );
  const withMyAccountmenuItems = useWithMyAccountMenuItems(
    mainMenuItems,
    intl,
    formattedRoutes,
    logout,
    isUserLoggedIn,
    isAABEnabled,
    isWishlistEnabled
  );
  const navMenuItems = React.useMemo(() => {
    const menuItems = isMyAccountEnabled ? withMyAccountmenuItems : mainMenuItems;
    const result = isMultilingualEnabled ? [...menuItems, createLocaleSwitcherMenuStructure(localeOptions || [], intl)] : menuItems;
    const previewLinkPrefix = `/site/_cmsinternal/${bloomreach.channel}`;
    const currentPath = pathname?.split("?")[0];
    const activeItems = result.filter(
      (menuItem) => checkIfActive(currentPath, (menuItem.link?.href || "").replace(previewLinkPrefix, ""))
    );
    const active = activeItems.sort((a, b) => isMoreSpecific(a.link?.href, b.link?.href) ? -1 : 1).shift();
    if (active?.link) {
      active.link = { ...active.link, active: true };
    }
    return result;
  }, [
    isMyAccountEnabled,
    withMyAccountmenuItems,
    mainMenuItems,
    isMultilingualEnabled,
    localeOptions,
    intl,
    bloomreach.channel,
    pathname
  ]);
  const handleSearch = React.useCallback(
    (query, type) => {
      if (!algolia) {
        return;
      }
      const { indexes, productIndex } = algolia;
      const storeIndex = indexes?.find((index) => index.type === "stores");
      const contentIndex = indexes?.find((index) => index.type === "content");
      switch (type) {
        case "products":
          window.location.assign(`${productIndex.targetPath}${encodeURIComponent(query)}`);
          break;
        case "stores":
          storeIndex && window.location.assign(`${storeIndex.targetPath}${encodeURIComponent(query)}`);
          break;
        case "content":
          contentIndex && window.location.assign(`${contentIndex.targetPath}${encodeURIComponent(query)}#pages`);
          break;
        default:
          console.warn(`Trying to perform search action for unknown Algolia index type: ${type}`);
      }
    },
    [algolia]
  );
  React.useEffect(() => {
    if ((!mounted || State.isUninitialized(authRef.current)) && State.isInitialized(auth)) {
      recoverSession();
    }
    setMounted(true);
    authRef.current = auth;
    setUserObject(isUserLoggedIn);
  }, [mounted, auth, recoverSession, isUserLoggedIn]);
  if ((!topMenu || !topMenuItems) && (!mainMenu || !mainMenuItems)) {
    console.error("No configuration or components found for Header in CMS model!");
    return null;
  }
  if (!headerLogo || !headerLogoMobile) return null;
  return /* @__PURE__ */ React.createElement(
    CmsHeader$1,
    {
      ...props,
      headerLogoLink: logoLink,
      headerMenuModel: topMenu,
      headerMenuItems: topMenuItems,
      navMenuModel: mainMenu,
      navMenuItems,
      appointmentMenuModel: appointmentMenu,
      appointmentMenuItems: appointmentMenuItemsWithAABconfig,
      myAccountButton,
      onSubmitSearch: handleSearch,
      isPreview,
      availableLocales: localeOptions,
      countrySwitcherOptions,
      headerLogo,
      headerLogoMobile,
      pageRoutes: routes,
      algoliaHeaderConfig: algolia,
      ordersUrl
    }
  );
};
function isMoreSpecific(nextLink = "", prevLink = "") {
  const isEqual = (nextLink.match(/\//g) || []).length === (prevLink.match(/\//g) || []).length;
  if (isEqual) {
    return nextLink.length > prevLink.length;
  } else {
    return (nextLink.match(/\//g) || []).length > (prevLink.match(/\//g) || []).length;
  }
}
function checkIfActive(currentPath = "", itemPath = "") {
  if (currentPath === "/" && itemPath === "/") {
    return true;
  } else {
    return currentPath.startsWith(itemPath);
  }
}
const createLocaleSwitcherMenuStructure = (availableLocales, intl) => {
  const localeSwitcherMenuLabel = getLocaleSwitcherMenuLabel(intl.locale);
  const localeMenuItems = getLocaleSwitcherData(availableLocales, false);
  const activeLocale = availableLocales.find(
    (item) => item.locale.replace("_", "-") === intl.locale
  );
  return {
    label: localeSwitcherMenuLabel,
    parameters: { menuKey: "locale-switcher" },
    childMenuItems: [
      {
        label: activeLocale ? activeLocale.chooseLanguage : localeSwitcherMenuLabel,
        childMenuItems: localeMenuItems
      }
    ]
  };
};
const getCountrySwitcherUrls = (content) => {
  if (!content?.imageLinkList) {
    return void 0;
  }
  return content.imageLinkList.map(
    (item) => ({
      image: item.image?.url ?? "",
      label: item.link?.label ?? "",
      url: item.link?.url ?? "",
      openInNewWindow: item.link?.openInNewWindow || false
    })
  );
};
const useCreateMyAccountMobileMenuStructure = (intl, formattedRoutes, logout, isLoggedIn, isAABEnabled, isWishlistEnabled) => {
  const myAccountMenuItems = useMyAccountMenuItems({
    logout,
    routes: formattedRoutes,
    isUserLoggedIn: isLoggedIn,
    isAabEnabled: isAABEnabled,
    isWishlistEnabled
  });
  const submenu = [
    {
      label: "",
      tealiumLabel: "Account",
      childMenuItems: formattedRoutes ? myAccountMenuItems : []
    }
  ];
  return {
    label: intl.getLabel("account.menu.myAccount"),
    link: {
      href: "/",
      type: "external"
    },
    parameters: {
      menuKey: "my-account"
    },
    childMenuItems: submenu
  };
};
const useWithMyAccountMenuItems = (navMenuItems, intl, formattedRoutes, logout, isLoggedIn, isAABEnabled, isWishlistEnabled) => {
  const myAccountMenuItems = useCreateMyAccountMobileMenuStructure(
    intl,
    formattedRoutes,
    logout,
    isLoggedIn,
    isAABEnabled,
    isWishlistEnabled
  );
  return [...navMenuItems || [], myAccountMenuItems];
};
export {
  CmsHeader as default
};
//# sourceMappingURL=Header.js.map
